import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix para ícones do Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Ícones personalizados profissionais e elegantes
const createCustomIcon = (iconType: 'vehicle' | 'destination' | 'location') => {
  let iconConfig = {
    size: [42, 42],
    symbol: '🚗',
    bgColor: '#3B82F6',
    borderColor: '#1E40AF',
    textColor: '#FFFFFF'
  };

  switch (iconType) {
    case 'vehicle':
      iconConfig = {
        size: [48, 48],
        symbol: '🚗',
        bgColor: '#1E293B', // Azul escuro profissional
        borderColor: '#0F172A',
        textColor: '#FFFFFF'
      };
      break;
    case 'destination':
      iconConfig = {
        size: [44, 44],
        symbol: '🎯', // Alvo de destino
        bgColor: '#7C3AED', // Roxo elegante
        borderColor: '#5B21B6',
        textColor: '#FFFFFF'
      };
      break;
    case 'location':
      iconConfig = {
        size: [38, 38],
        symbol: '📍',
        bgColor: '#059669', // Verde elegante
        borderColor: '#047857',
        textColor: '#FFFFFF'
      };
      break;
  }
  
  return L.divIcon({
    className: 'custom-marker-elegant',
    html: `
      <div style="
        background: linear-gradient(145deg, ${iconConfig.bgColor} 0%, ${iconConfig.borderColor} 100%);
        width: ${iconConfig.size[0]}px;
        height: ${iconConfig.size[1]}px;
        border-radius: 12px;
        border: 2px solid rgba(255,255,255,0.9);
        box-shadow: 
          0 6px 20px rgba(0,0,0,0.15),
          0 0 0 1px rgba(255,255,255,0.2),
          inset 0 1px 0 rgba(255,255,255,0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        position: relative;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        backdrop-filter: blur(10px);
      ">
        ${iconConfig.symbol}
        <div style="
          position: absolute;
          bottom: -6px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-top: 6px solid ${iconConfig.bgColor};
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
        "></div>
      </div>
    `,
    iconSize: iconConfig.size as [number, number],
    iconAnchor: [iconConfig.size[0] / 2, iconConfig.size[1] + 6],
  });
};

interface MapComponentProps {
  origem?: { lat: number; lng: number };
  destino: { lat: number; lng: number };
  fases?: Array<{
    id: number;
    nome_fase: string;
    endereco_destino: string;
    status_fase: string;
    ordem: number;
    lat?: number;
    lng?: number;
  }>;
  showTimeline?: boolean;
  isTracking?: boolean;
  // Callback para informar dados da rota calculada
  onRouteCalculated?: (route: { distance: number; duration: number; instructions: string[] }) => void;
}

const MapComponent: React.FC<MapComponentProps> = ({ 
  origem, 
  destino, 
  fases = [],
  showTimeline = true,
  isTracking = false,
  onRouteCalculated
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const routeLineRef = useRef<L.Polyline | null>(null);
  const carMarkerRef = useRef<L.Marker | null>(null); // Ref para o marcador do carro
  const destinoMarkerRef = useRef<L.Marker | null>(null); // Ref para o marcador de destino
  const lastRecalcRef = useRef<number>(0);
  const isCalculatingRef = useRef<boolean>(false);
  const [currentPosition, setCurrentPosition] = useState<{ lat: number; lng: number } | null>(origem || null);
  const [route, setRoute] = useState<{ distance: number; duration: number; instructions: string[] } | null>(null);
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);

  // Função para calcular rota usando OSRM (OpenStreetMap nativo)
  const calculateRoute = React.useCallback(async (map: L.Map, force = false) => {
    // Usar posição atual se disponível, senão usar origem inicial
    const posicaoAtual = currentPosition || origem;
    if (!posicaoAtual || !destino || !map) return;
    
    // Evitar recálculo se já está calculando
    if (isCalculatingRef.current && !force) {
      console.log('⏳ Rota já está sendo calculada, aguardando...');
      return;
    }
    
    isCalculatingRef.current = true;
    setIsCalculatingRoute(true);
    console.log('🗺️ Calculando rota usando OSRM (OpenStreetMap nativo)...');
    console.log('📍 Origem (atual):', posicaoAtual);
    console.log('📍 Destino:', destino);
    
    try {
      // Usar OSRM diretamente (OpenStreetMap)
      const response = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${posicaoAtual.lng},${posicaoAtual.lat};${destino.lng},${destino.lat}?overview=full&geometries=geojson&steps=true`
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('🗺️ Resposta do OSRM:', data);
        
        if (data.routes && data.routes[0]) {
          const routeData = data.routes[0];
          
          // Limpar rota anterior
          if (routeLineRef.current) {
            map.removeLayer(routeLineRef.current);
          }
          
          // OSRM já retorna no formato correto para Leaflet
          const latLngPoints = routeData.geometry.coordinates.map((coord: number[]) => {
            return [coord[1], coord[0]]; // [lat, lng]
          });
          
          console.log('🗺️ Pontos da rota OSRM:', latLngPoints);
          console.log('🗺️ Origem:', [posicaoAtual.lat, posicaoAtual.lng]);
          console.log('🗺️ Destino:', [destino.lat, destino.lng]);
          
                     // Criar linha da rota (azul profissional com transparência)
           const routeLine = L.polyline(latLngPoints, {
             color: '#3B82F6',        // Azul elegante
             weight: 5,               // Espessura mais sutil
             opacity: 0.8,            // Transparência profissional
             dashArray: '12, 8',      // Traços mais suaves
             lineCap: 'round',        // Pontas arredondadas
             lineJoin: 'round'        // Junções arredondadas
           });
          
          // Adicionar ao mapa
          routeLine.addTo(map);
          routeLineRef.current = routeLine;
          
          // Extrair informações da rota
          const routeInfo = {
            distance: routeData.distance / 1000, // km
            duration: routeData.duration / 60,   // minutos
            instructions: routeData.legs[0].steps.map((step: any) => step.maneuver.instruction) || []
          };
          
          setRoute(routeInfo);
          onRouteCalculated?.(routeInfo);
          
          console.log('✅ Rota OSRM calculada e desenhada:', routeInfo);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao calcular rota OSRM:', error);
      
      // Fallback: tentar OpenRouteService
      console.log('🔄 Tentando OpenRouteService como fallback...');
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002/api';
        const posicaoAtual = currentPosition || origem;
        const fallbackResponse = await fetch(
          `${apiUrl}/routing/route?start=${posicaoAtual.lng},${posicaoAtual.lat}&end=${destino.lng},${destino.lat}&profile=driving-car`
        );
        
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          // ... lógica de fallback
        }
      } catch (fallbackError) {
        console.error('❌ Fallback também falhou:', fallbackError);
      }
    } finally {
      isCalculatingRef.current = false;
      setIsCalculatingRoute(false);
    }
  }, [currentPosition, origem, destino]);

  // Criar mapa apenas uma vez
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Criar mapa básico
    const map = L.map(mapRef.current).setView([destino.lat, destino.lng], 13);
    mapInstanceRef.current = map;

    // Adicionar camada OpenStreetMap
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);

    // Marcador de destino (bandeirinha de chegada) - criar apenas uma vez
    destinoMarkerRef.current = L.marker([destino.lat, destino.lng], {
      icon: createCustomIcon('destination')
    })
      .addTo(map)
      .bindPopup(`
        <div class="text-center p-2">
          <div class="flex items-center justify-center mb-2">
            🎯 <strong class="ml-2">Destino</strong>
          </div>
          <div class="text-sm text-gray-600">
            Ponto de chegada
          </div>
          <div class="text-xs text-gray-500">
            ${destino.lat.toFixed(6)}, ${destino.lng.toFixed(6)}
          </div>
        </div>
      `);

    // Marcador de origem inicial (veículo do motorista) - criar apenas se houver origem
    if (origem) {
      carMarkerRef.current = L.marker([origem.lat, origem.lng], {
        icon: createCustomIcon('vehicle')
      })
        .addTo(map)
        .bindPopup(`
          <div class="text-center p-2">
            <div class="flex items-center justify-center mb-2">
              🚗 <strong class="ml-2">Veículo</strong>
            </div>
            <div class="text-sm text-gray-600">
              Posição atual do motorista
            </div>
            <div class="text-xs text-gray-500">
              ${origem.lat.toFixed(6)}, ${origem.lng.toFixed(6)}
            </div>
          </div>
        `);

      // Ajustar view para mostrar ambos os pontos
      const bounds = L.latLngBounds([origem, destino]);
      map.fitBounds(bounds, { padding: [20, 20] });
    }

    // Calcular rota inicial após 1 segundo (apenas uma vez)
    const timeoutId = setTimeout(() => {
      if (mapInstanceRef.current) {
        calculateRoute(mapInstanceRef.current, true);
      }
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
      // Limpar marcadores
      if (carMarkerRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(carMarkerRef.current);
        carMarkerRef.current = null;
      }
      if (destinoMarkerRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(destinoMarkerRef.current);
        destinoMarkerRef.current = null;
      }
      if (routeLineRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(routeLineRef.current);
        routeLineRef.current = null;
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Criar mapa apenas uma vez quando o componente monta

  // Sincronizar currentPosition com a prop origem quando ela mudar (vem do FaseCard via useLocationTracking)
  useEffect(() => {
    if (origem) {
      setCurrentPosition(origem);
    }
  }, [origem]);

  // Efeito para atualizar posição do carro quando rastreando
  // NOTA: A localização real já é obtida pelo hook useLocationTracking no FaseCard
  // Este efeito apenas atualiza o marcador no mapa quando currentPosition mudar
  useEffect(() => {
    if (!mapInstanceRef.current || !currentPosition) return;

    // Verificar se o marcador já existe usando a ref
    if (!carMarkerRef.current) {
      // Criar marcador do carro apenas se não existir
      carMarkerRef.current = L.marker([currentPosition.lat, currentPosition.lng], {
        icon: createCustomIcon('vehicle')
      })
        .addTo(mapInstanceRef.current)
        .bindPopup(`
          <div class="text-center p-2">
            <strong>Seu Veículo</strong><br>
            <small>Posição atual</small><br>
            <small class="text-gray-500">${currentPosition.lat.toFixed(6)}, ${currentPosition.lng.toFixed(6)}</small>
          </div>
        `);
      console.log('🚗 Marcador do carro criado pela primeira vez');
    } else {
      // Atualizar posição do marcador existente (não criar novo)
      carMarkerRef.current.setLatLng([currentPosition.lat, currentPosition.lng]);
      
      // Atualizar popup com nova posição
      carMarkerRef.current.setPopupContent(`
        <div class="text-center p-2">
          <strong>Seu Veículo</strong><br>
          <small>Posição atual</small><br>
          <small class="text-gray-500">${currentPosition.lat.toFixed(6)}, ${currentPosition.lng.toFixed(6)}</small>
        </div>
      `);
      console.log('📍 Posição do carro atualizada:', currentPosition);
    }
    
    // Recalcular rota se houver destino (usando posição atual ao invés de origem inicial)
    // Recalcular apenas se já existe rota e não está calculando (para evitar loops)
    if (destino && routeLineRef.current && !isCalculatingRef.current) {
      // Aguardar um pouco antes de recalcular para evitar spam
      const now = Date.now();
      if (now - lastRecalcRef.current > 15000) { // Recalcular no máximo a cada 15 segundos
        lastRecalcRef.current = now;
        const map = mapInstanceRef.current;
        
        // Remover rota antiga
        map.removeLayer(routeLineRef.current);
        routeLineRef.current = null;
        
        // Calcular nova rota com posição atualizada
        calculateRoute(map, true);
      }
    }
  }, [currentPosition, destino, calculateRoute]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pendente': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Em Rota': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Concluída': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Pendente': return '⏳';
      case 'Em Rota': return '🚗';
      case 'Concluída': return '✅';
      default: return '📍';
    }
  };

  return (
    <div className="space-y-4">
      {/* Mapa Principal */}
      <div className="map-container">
        <div ref={mapRef} className="h-96 w-full rounded-lg shadow-lg" />
        
                       {/* Indicador de rastreamento ativo */}
               {isTracking && (
                 <div className="absolute top-4 left-4 bg-green-500 text-white rounded-lg px-3 py-2">
                   <div className="flex items-center space-x-2">
                     <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                     <span className="text-sm font-medium">Rastreando em tempo real</span>
                   </div>
                 </div>
               )}

               {/* Indicador de cálculo de rota */}
               {isCalculatingRoute && (
                 <div className="absolute top-4 right-4 bg-blue-500 text-white rounded-lg px-3 py-2">
                   <div className="flex items-center space-x-2">
                     <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                     <span className="text-sm font-medium">Calculando rota...</span>
                   </div>
                 </div>
               )}

               {/* Informações da rota */}
               {route && (
                 <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3">
                   <div className="flex items-center space-x-4">
                     <div className="text-center">
                       <div className="text-lg font-bold text-blue-600">
                         {route.distance > 0 ? `${route.distance.toFixed(1)} km` : '-'}
                       </div>
                       <div className="text-xs text-gray-600">Distância</div>
                     </div>
                     <div className="text-center">
                       <div className="text-lg font-bold text-green-600">
                         {route.duration > 0 ? `${Math.round(route.duration)} min` : '-'}
                       </div>
                       <div className="text-xs text-gray-600">Tempo</div>
                     </div>
                   </div>
                 </div>
               )}
      </div>

      {/* Timeline das Fases */}
      {showTimeline && fases.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline das Fases</h3>
          
          <div className="relative">
            {/* Linha vertical da timeline */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-blue-200"></div>
            
            <div className="space-y-4">
              {fases.map((fase, index) => (
                <div key={fase.id} className="relative flex items-start space-x-4">
                  {/* Círculo da fase */}
                  <div className={`
                    relative z-10 w-12 h-12 rounded-full border-4 border-white shadow-lg flex items-center justify-center text-lg
                    ${fase.status_fase === 'Pendente' ? 'bg-yellow-400' : 
                      fase.status_fase === 'Em Rota' ? 'bg-blue-500' : 
                      fase.status_fase === 'Concluída' ? 'bg-green-500' : 'bg-gray-400'}
                  `}>
                    {getStatusIcon(fase.status_fase)}
                  </div>
                  
                  {/* Conteúdo da fase */}
                  <div className="flex-1">
                    <div className={`
                      p-3 rounded-lg border-2 ${getStatusColor(fase.status_fase)}
                    `}>
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">{fase.nome_fase}</h4>
                        <span className="text-xs font-medium">Fase {fase.ordem}</span>
                      </div>
                      <p className="text-sm mt-1 opacity-75">{fase.endereco_destino}</p>
                      <div className="flex items-center space-x-2 mt-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(fase.status_fase)}`}>
                          {fase.status_fase}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapComponent;
